/**
 * Activity client-side types + fail-closed parsers (SYN-1516, spec §7.6).
 *
 * The Command BFF owns the DTO: it enriches contract names (the Atlas lineage
 * detail shape lacks them) and strips trace URLs for non-staff server-side.
 * These parsers therefore trust the shape's contract — they never re-derive
 * credits, never compute trace visibility — they only defensively narrow the
 * JSON into typed objects so the UI renders no `undefined`s.
 */

export type RunOutcome = "completed" | "failed" | "running" | "pending";
export type ActivityScope = "self" | "org";

export interface RunSummary {
  runId: string;
  outcome: RunOutcome;
  /** Plain sentence the row renders (already buyer-clean from the BFF). */
  sentence: string;
  routineName: string;
  /** Atlas-authoritative credits, or null when not yet tallied. */
  credits: number | null;
  occurredAt: string | null;
}

export interface RunStep {
  label: string;
  outcome: string;
  occurredAt: string | null;
}

export interface ActorChainItem {
  label: string;
  principalId: string;
  principalType: string;
  delegationOrigin: string | null;
  delegationDepth: number | null;
  parentSpanId: string | null;
  parentPrincipalId: string | null;
  outcome: string | null;
}

export interface PolicyEvidenceItem {
  label: string;
  outcome: string;
  capabilityId: string | null;
  denyReason: string | null;
  detail: string | null;
  traceId: string | null;
  spanId: string | null;
}

export interface RunDetail {
  runId: string;
  outcome: RunOutcome;
  routineName: string;
  narrative: string;
  credits: number | null;
  durationMs: number | null;
  occurredAt: string | null;
  steps: RunStep[];
  actorChain?: ActorChainItem[];
  policyEvidence?: PolicyEvidenceItem[];
  /** Present only for staff (the BFF strips it for everyone else). */
  traceUrl: string | null;
  technical: {
    runReference: string;
    contractId: string | null;
    traceId: string | null;
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

function nullableStr(value: unknown): string | null {
  return typeof value === "string" && value ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function optionalArray<T>(raw: unknown, parser: (value: unknown) => T | null): T[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  return raw.map(parser).filter((row): row is T => row !== null);
}

function normalizeOutcome(value: unknown): RunOutcome {
  switch (value) {
    case "completed":
      return "completed";
    case "failed":
      return "failed";
    case "running":
      return "running";
    default:
      return "pending";
  }
}

export function parseRunSummary(raw: unknown): RunSummary | null {
  const r = asRecord(raw);
  const runId = str(r.runId);
  if (!runId) {
    return null;
  }
  return {
    runId,
    outcome: normalizeOutcome(r.outcome),
    sentence: str(r.sentence) ?? "A routine ran",
    routineName: str(r.routineName) ?? "This routine",
    credits: num(r.credits),
    occurredAt: nullableStr(r.occurredAt),
  };
}

export function parseRunSummaries(raw: unknown): RunSummary[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(parseRunSummary).filter((row): row is RunSummary => row !== null);
}

function parseStep(raw: unknown): RunStep | null {
  const r = asRecord(raw);
  const label = str(r.label);
  if (!label) {
    return null;
  }
  return {
    label,
    outcome: str(r.outcome) ?? "",
    occurredAt: nullableStr(r.occurredAt),
  };
}

function parseActorChainItem(raw: unknown): ActorChainItem | null {
  const r = asRecord(raw);
  const label = str(r.label);
  const principalId = str(r.principalId);
  const principalType = str(r.principalType);
  if (!label || !principalId || !principalType) {
    return null;
  }
  return {
    label,
    principalId,
    principalType,
    delegationOrigin: nullableStr(r.delegationOrigin),
    delegationDepth: num(r.delegationDepth),
    parentSpanId: nullableStr(r.parentSpanId),
    parentPrincipalId: nullableStr(r.parentPrincipalId),
    outcome: nullableStr(r.outcome),
  };
}

function parsePolicyEvidenceItem(raw: unknown): PolicyEvidenceItem | null {
  const r = asRecord(raw);
  const label = str(r.label);
  const outcome = str(r.outcome);
  if (!label || !outcome) {
    return null;
  }
  return {
    label,
    outcome,
    capabilityId: nullableStr(r.capabilityId),
    denyReason: nullableStr(r.denyReason),
    detail: nullableStr(r.detail),
    traceId: nullableStr(r.traceId),
    spanId: nullableStr(r.spanId),
  };
}

/** who-holds / who-assigned chip for a card (skill/agent/contract). */
export interface ArtifactAssigneeChip {
  principalId: string;
  roleName: string;
}

/** The closed set of outcome strings the BFF emits. Anything else is honest
 * "no outcome yet" (null), never coerced to a misleading chip. normalizeOutcome
 * maps unknowns to "pending", so we gate the input BEFORE calling it. */
const KNOWN_OUTCOMES = new Set<RunOutcome>(["completed", "failed", "running", "pending"]);

/**
 * Per-card activity summary (spec §8). Operational data, kept distinct from
 * ArtifactAccess authority (§10.4 axis separation). `lastOutcome` only resolves
 * for a recognised outcome string so card chips match the activity feed and
 * detail page; credits are NEVER on this shape (Command renders credits only
 * from an authoritative value, never derived). `tracked:false` is the honest
 * absence state for types with no run table, the card shows "Usage not tracked
 * yet", never a fabricated zero.
 */
export interface ArtifactActivitySummary {
  runCount: number;
  lastRunAt: string | null;
  lastOutcome: RunOutcome | null;
  assignees: ArtifactAssigneeChip[] | null;
  tracked: boolean;
}

function parseAssignees(raw: unknown): ArtifactAssigneeChip[] | null {
  if (!Array.isArray(raw)) {
    return null;
  }
  const chips: ArtifactAssigneeChip[] = [];
  for (const entry of raw) {
    const r = asRecord(entry);
    const principalId = str(r.principalId);
    const roleName = str(r.roleName);
    if (principalId && roleName) {
      chips.push({ principalId, roleName });
    }
  }
  return chips;
}

export function parseArtifactActivitySummary(raw: unknown): ArtifactActivitySummary | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const r = asRecord(raw);
  const runCountRaw = num(r.runCount);
  const lastOutcomeRaw =
    typeof r.lastOutcome === "string" && KNOWN_OUTCOMES.has(r.lastOutcome as RunOutcome)
      ? (r.lastOutcome as RunOutcome)
      : null;
  return {
    runCount: runCountRaw ?? 0,
    lastRunAt: nullableStr(r.lastRunAt),
    // Gated above: only a recognised outcome resolves; an absent/unknown value
    // stays null (an honest "no outcome yet"), never coerced to "pending".
    lastOutcome: lastOutcomeRaw ? normalizeOutcome(lastOutcomeRaw) : null,
    assignees: parseAssignees(r.assignees),
    tracked: r.tracked === true,
  };
}

export function parseRunDetail(raw: unknown): RunDetail | null {
  const r = asRecord(raw);
  const runId = str(r.runId);
  if (!runId) {
    return null;
  }
  const technical = asRecord(r.technical);
  const steps = Array.isArray(r.steps)
    ? r.steps.map(parseStep).filter((s): s is RunStep => s !== null)
    : [];
  const actorChain = optionalArray(r.actorChain, parseActorChainItem);
  const policyEvidence = optionalArray(r.policyEvidence, parsePolicyEvidenceItem);
  return {
    runId,
    outcome: normalizeOutcome(r.outcome),
    routineName: str(r.routineName) ?? "This routine",
    narrative: str(r.narrative) ?? "This run has no summary yet.",
    credits: num(r.credits),
    durationMs: num(r.durationMs),
    occurredAt: nullableStr(r.occurredAt),
    steps,
    ...(actorChain ? { actorChain } : {}),
    ...(policyEvidence ? { policyEvidence } : {}),
    traceUrl: nullableStr(r.traceUrl),
    technical: {
      runReference: str(technical.runReference) ?? runId,
      contractId: nullableStr(technical.contractId),
      traceId: nullableStr(technical.traceId),
    },
  };
}
